import React, { useEffect, useState } from "react";
import ReactTable from "react-table";
import { UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faSearch,
  faShoppingCart
} from "@fortawesome/free-solid-svg-icons";
import s from "./styles.scss";
import ExternoDetalle from "../../../types/ExternoDetalle";
import permissionCodes from "../../../utils/permissionCodes";
import { userHasPermission } from "../../../utils/accessTree";

const {
  PERSONA_EXTERNA_VER_DETALLE: VER_DETALLE,
  MENU_CARRITO,
  MENU_INVITADO,
  MENU_TOTAL_PASS,
  MENU_MEMBRESIA_SOCIO
} = permissionCodes;

export interface TableProps {
  page: Page<ExternoDetalle>;
  loading: boolean;
  onFetchData: ({ page, pageSize }) => void;
}

export const MemoTable = React.memo(
  ({ page, loading, onFetchData }: TableProps) => {
    const [columns, setColumns] = useState(columnsFormatter());
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    useEffect(() => {
      setColumns(columnsFormatter());
    }, []);

    useEffect(() => {
      if (page) {
        setPageNumber(page.number || 0);
        setTotalPages(page.totalPages || 0);
      }
    }, [page]);

    const mostrados = page && page.numberOfElements;
    const totales = page && page.totalElements;
    return (
      <>
        {/* Botón para exportar */}
        <div className="row">
          {!!page && !!page.content && !!page.content.length ? (
            <>
              <div className="col-6 p-0">
                <span>
                  Personas mostradas: {mostrados} de {totales}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="col-6 p-0">
                <span>Personas mostradas: 0</span>
              </div>
            </>
          )}
        </div>

        <div className="row mt-2">
          <div style={{ width: "100%" }}>
            <ReactTable
              manual
              data={(page && page.content) || []}
              columns={columns}
              loading={loading}
              defaultPageSize={50}
              className="-striped -highlight"
              onFetchData={onFetchData}
              page={pageNumber}
              pages={totalPages}
              showPagination={true}
              showPageSizeOptions={false}
              getTrProps={() => ({ style: { height: "30px" } })}
              sortable={false}
            />
          </div>
        </div>
      </>
    );
  }
);

function columnsFormatter() {
  return [
    {
      Header: "Id Persona",
      id: "idPersona",
      className: s.tdClear,
      width: 80,
      accessor: simpleAccessor("idPersona")
    },
    {
      Header: "Nombre",
      id: "nombreCompleto",
      className: s.tdClear,
      accessor: simpleAccessor("nombreCompleto")
    },
    {
      Header: "Membresía",
      id: "membresia",
      width: 144,
      className: s.tdClear,
      accessor: (row: ExternoDetalle) => {
        if (!!row.idMembresiaSocio) {
          const href = `/membresias-socios/${row.idMembresiaSocio}?idClub=${row.idClubBase}&idPublico=${row.idPublico}`;
          const label = `${row.idPublico} de ${row.clubBase}`;
          return userHasPermission(MENU_MEMBRESIA_SOCIO) ? (
            <div className={calcStyle(row)}>
              <a href={href} target="_blank" rel="noreferrer">
                {label}
              </a>
            </div>
          ) : (
            <div className={calcStyle(row)}>{label}</div>
          );
        }

        return <div className={calcStyle(row)}>-</div>;
      }
    },
    {
      Header: "Invitado",
      id: "invitado",
      className: s.tdClear,
      width: 160,
      accessor: (row: ExternoDetalle) => {
        if (!!row.idInvitado) {
          const href = `/invitado-especial/${row.idInvitado}`;
          return userHasPermission(MENU_INVITADO) ? (
            <div className={calcStyle(row)}>
              <a href={href} target="_blank" rel="noreferrer">
                {enumFormatter(row.tipoInvitado)}
              </a>
            </div>
          ) : (
            <div className={calcStyle(row)}>
              {enumFormatter(row.tipoInvitado)}
            </div>
          );
        }
        return <div className={calcStyle(row)}>-</div>;
      }
    },
    {
      Header: "TotalPass",
      width: 70,
      id: "totalPass",
      className: s.tdClear,
      accessor: (row: ExternoDetalle) => {
        if (row.totalPass) {
          const href = `/total-pass-detalle/${row.idPersona}`;
          return userHasPermission(MENU_TOTAL_PASS) ? (
            <div className={calcStyle(row)}>
              <a href={href} target="_blank" rel="noreferrer">
                Sí (Ver)
              </a>
            </div>
          ) : (
            <div className={calcStyle(row)}>Sí</div>
          );
        }
        return <div className={calcStyle(row)}>-</div>;
      }
    },
    {
      Header: "Empleado",
      id: "empleado",
      width: 80,
      className: s.tdClear,
      accessor: (row: ExternoDetalle) => (
        <div className={calcStyle(row)}>
          {row.idAuthUser ? row.numeroEmpleado : "-"}&nbsp;
        </div>
      )
    },
    {
      Header: "CURP",
      id: "curp",
      width: 160,
      className: s.tdClear,
      accessor: simpleAccessor("curp")
    },
    {
      Header: "Tipo",
      id: "tipo",
      width: 128,
      className: s.tdClear,
      accessor: enumAccessor("tipo")
    },
    {
      Header: "Blacklist",
      id: "vetado",
      width: 70,
      className: s.tdClear,
      accessor: (row: ExternoDetalle) => (
        <div className={calcStyle(row)}>{row.vetado ? "Sí" : "No"}&nbsp;</div>
      )
    },
    {
      Header: "Eliminada",
      id: "eliminado",
      width: 70,
      className: s.tdClear,
      accessor: (row: ExternoDetalle) => (
        <div className={calcStyle(row)}>
          {row.eliminado ? "Sí" : "No"}&nbsp;
        </div>
      )
    },
    {
      id: "accion",
      className: s.tdClear,
      width: 88,
      accessor: (row: ExternoDetalle) => {
        const idPrefijo = "ext-" + row.idPersona;
        const idVerPersona = idPrefijo + "-ver-persona";
        const idEditar = idPrefijo + "-editar";
        const idCarrito = idPrefijo + "-carrito";

        return (
          <div className={`${calcStyle(row)} text-center`}>
            <span
              onClick={() => openToEdit(row)}
              className={s.btn}
              id={idVerPersona}
            >
              <FontAwesomeIcon icon={faEye} color="#0073e6" />
              <UncontrolledTooltip placement="bottom" target={idVerPersona}>
                Ver persona
              </UncontrolledTooltip>
            </span>

            {userHasPermission(VER_DETALLE) && (
              <span
                onClick={() =>
                  window.open(`/persona-detalle/${row.idPersona}`, "_blank")
                }
                className={s.btn}
                id={idEditar}
              >
                <FontAwesomeIcon icon={faSearch} color="#0073e6" />
                <UncontrolledTooltip placement="bottom" target={idEditar}>
                  Datos de Persona
                </UncontrolledTooltip>
              </span>
            )}

            {userHasPermission(MENU_CARRITO) && (
              <span
                onClick={() =>
                  window.open(`/productos/carrito/${row.idPersona}`, "_blank")
                }
                className={s.btn}
                id={idCarrito}
              >
                <FontAwesomeIcon icon={faShoppingCart} color="#0073e6" />
                <UncontrolledTooltip placement="bottom" target={idCarrito}>
                  Ver carrito
                </UncontrolledTooltip>
              </span>
            )}
          </div>
        );
      }
    }
  ];
}

function calcStyle(row: ExternoDetalle): string {
  if (row.eliminado) {
    return `${s.eliminado} ${s.field}`;
  }

  if (row.totalPass) {
    return `${s.totalPass} ${s.field}`;
  }

  return `${s.field}`;
}

function simpleAccessor(key: keyof ExternoDetalle) {
  return (row: ExternoDetalle) => {
    return <div className={calcStyle(row)}>{row[key]}&nbsp;</div>;
  };
}

function enumAccessor(accessor: string) {
  return row => {
    const sentence = enumFormatter(row[accessor]);
    return <div className={calcStyle(row)}>{sentence}</div>;
  };
}

function enumFormatter(value: string) {
  if (!value) {
    return "";
  }
  const sentence = value.toLowerCase().split("_");

  for (var i = 0; i < sentence.length; i++) {
    if (sentence[i]) {
      sentence[i] = sentence[i][0].toUpperCase() + sentence[i].slice(1) + " ";
    }
  }

  return sentence;
}

function openToEdit(selectedPerson: { tipo: string; idPersona: number }) {
  if (!!selectedPerson) {
    const { tipo, idPersona: personaId } = selectedPerson as any;
    let url: string = null;
    switch (tipo) {
      case "CLIENTE_TITULAR":
      case "CLIENTE_INTEGRANTE":
      case "CLIENTE":
        url = `/socio/${personaId}/detalle`;
        break;
      case "PROSPECTO":
        url = `/prospecto/${personaId}/detalle`;
        break;
      case "CONCESIONARIO":
        url = `/concesionarios/${personaId}/detalle`;
        break;
      case "CORPORATIVO":
        url = `/corporativos/${personaId}/detalle`;
        break;
      case "EXTERNO":
        url = `/externo/${personaId}/detalle`;
        break;
      case "EMPLEADO":
        url = `/empleado/${personaId}/detalle`;
        break;
    }

    if (!!url) {
      window.open(url, "_blank");
    }
  }
}
